import { useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CircleAlert, CircleCheck, X } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import {
  ANNUAL_VOLUMES,
  REGIONS,
  type ContactRole,
  type IntakePrefill,
  type Region,
} from '@/lib/api-types'
import type { FieldErrors } from '@/lib/zod-errors'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldError, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { RadioGroup } from '@/components/ui/radio-group'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { Wordmark } from '@/components/wordmark'
import { ContactDirectory, ProcessSteps, WelcomeIntro } from './welcome-content'
import {
  MAX_ADDITIONAL_CONTACTS,
  MAX_ADDITIONAL_SHIP_TOS,
  buildIntakePayload,
  emptyAdditionalContact,
  emptyShipTo,
  initialIntakeForm,
  validateIntake,
  type AdditionalContactRow,
  type IntakeFormState,
} from './intake-schema'

/**
 * /intake/:token — the no-login page a new customer's welcome-packet link
 * lands on (outside AuthGate; the token IS the authorization). The welcome
 * content rides above the form so the page reads as the packet, not a bare
 * questionnaire. Error states stay generic on purpose — an expired or forged
 * link never names anyone.
 */

const LINK_ERROR_COPY = {
  expired: {
    heading: 'This link has expired.',
    body: 'Welcome-packet links only work for a limited time. Reply to the email you received and Green Line will send a fresh one — it takes them one click.',
  },
  invalid: {
    heading: 'This link isn’t valid.',
    body: 'It may have been trimmed by your email client. Open the original email and use the full link.',
  },
} as const

/** One line per fixed role, so the four blocks explain themselves. */
const ROLE_COPY: Record<ContactRole, string> = {
  Ordering: 'Sends purchase orders and receives order confirmations.',
  Technical: 'Receives specifications, documentation, and technical answers.',
  'Accounts Payable': 'Receives invoices and statements.',
  Samples: 'Receives sample shipments and the documents that travel with them.',
}

export function IntakePage() {
  const { token = '' } = useParams()
  const [outcome, setOutcome] = useState<'sent' | 'already' | null>(null)

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['intake', token],
    queryFn: ({ signal }) => api.publicGetIntake(token, { signal }),
    // 404/410 are terminal answers, not flakes — surface them immediately.
    retry: false,
    staleTime: Infinity,
  })

  let content: ReactNode
  if (outcome === 'sent') {
    content = <IntakeSent />
  } else if (outcome === 'already' || (data && data.status === 'completed')) {
    content = (
      <TerminalCard
        icon={<CircleCheck aria-hidden="true" className="size-6 text-brand" />}
        heading="This intake was already submitted — thank you."
        body="Your details are with Green Line, and nothing else is needed. Your rep will be in touch on next steps."
      />
    )
  } else if (isPending) {
    content = (
      <div className="flex justify-center pt-[10vh]">
        <Spinner label="Opening your welcome packet…" />
      </div>
    )
  } else if (isError) {
    const kind = error instanceof ApiError && error.status === 410 ? 'expired' : 'invalid'
    content = (
      <TerminalCard
        icon={<CircleAlert aria-hidden="true" className="size-6 text-rust" />}
        heading={LINK_ERROR_COPY[kind].heading}
        body={LINK_ERROR_COPY[kind].body}
      />
    )
  } else {
    content = (
      <IntakeForm
        token={token}
        companyName={data.companyName}
        contactName={data.contactName}
        prefill={data.prefill}
        onDone={setOutcome}
      />
    )
  }

  return (
    <div className="flex min-h-screen flex-col bg-snow">
      <header className="mx-auto flex h-14 w-full max-w-5xl items-center px-6">
        <Wordmark />
      </header>
      <main className="mx-auto w-full max-w-form flex-1 px-6 pb-24 pt-8">{content}</main>
    </div>
  )
}

function TerminalCard({
  icon,
  heading,
  body,
}: {
  icon: ReactNode
  heading: string
  body: string
}) {
  return (
    <div className="flex justify-center pt-[8vh]">
      <Card padding="none" className="w-full max-w-md p-8">
        {icon}
        <h1 className="mt-4 text-xl font-semibold tracking-wordmark text-ink">{heading}</h1>
        <p className="mt-2 text-sm leading-relaxed text-inkMid">{body}</p>
      </Card>
    </div>
  )
}

/** The success terminal — what Green Line does next, in order. */
function IntakeSent() {
  // Phase 2: auto-chain the credit application from a submitted intake
  // (send the /credit invite automatically). For v1 Green Line sends it
  // manually, so the copy only promises that it will follow.
  const nextSteps = [
    'We set up your account on our side — nothing more is needed from you.',
    'Your Green Line rep follows up on samples and first-order details.',
    'If you want payment terms, a credit application will come your way from Green Line.',
  ]
  return (
    <div className="flex justify-center pt-[8vh]">
      <Card padding="none" className="w-full max-w-lg p-8">
        <CircleCheck aria-hidden="true" className="size-6 text-brand" />
        <h1 className="mt-4 text-xl font-semibold tracking-wordmark text-ink">
          Intake sent — welcome aboard.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-inkMid">
          Your Green Line rep has your details. Here&rsquo;s what happens next:
        </p>
        <ol className="mt-4 space-y-2">
          {nextSteps.map((step, index) => (
            <li key={step} className="flex items-baseline gap-3">
              <span aria-hidden="true" className="w-4 shrink-0 font-mono text-[0.6875rem] text-mute">
                {index + 1}
              </span>
              <span className="text-sm leading-relaxed text-inkMid">{step}</span>
            </li>
          ))}
        </ol>
        <p className="mt-4 text-sm leading-relaxed text-mute">
          Questions in the meantime? Write{' '}
          <a
            href="mailto:sales@greenlineingredients.com"
            className="rounded-sm font-mono text-[0.8125rem] text-brand underline-offset-4 hover:underline"
          >
            sales@greenlineingredients.com
          </a>
          . You can close this page.
        </p>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The open form
// ---------------------------------------------------------------------------

let rowSeq = 0

function IntakeForm({
  token,
  companyName,
  contactName,
  prefill,
  onDone,
}: {
  token: string
  companyName: string
  contactName: string
  prefill: IntakePrefill
  onDone: (outcome: 'sent' | 'already') => void
}) {
  const [form, setForm] = useState<IntakeFormState>(() => initialIntakeForm(prefill, contactName))
  const [attempted, setAttempted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Errors are DERIVED: once a send was attempted, the zod check re-runs on
  // every keystroke so messages clear as fields are fixed.
  const errors: FieldErrors = attempted ? validateIntake(form) : {}

  const mutation = useMutation({
    mutationFn: () => api.publicSubmitIntake(token, buildIntakePayload(form)),
    onSuccess: () => {
      onDone('sent')
      window.scrollTo({ top: 0 })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        onDone('already')
        return
      }
      setSubmitError('That didn’t send. Everything you entered is still here — try again.')
    },
  })

  function send() {
    if (Object.keys(validateIntake(form)).length > 0) {
      setAttempted(true)
      return
    }
    setSubmitError(null)
    mutation.mutate()
  }

  function patchCompany(changes: Partial<IntakeFormState['company']>) {
    setForm((current) => ({ ...current, company: { ...current.company, ...changes } }))
  }

  function patchRoleContact(index: number, changes: Partial<IntakeFormState['roleContacts'][number]>) {
    setForm((current) => ({
      ...current,
      roleContacts: current.roleContacts.map((block, i) =>
        i === index ? { ...block, ...changes } : block,
      ),
    }))
  }

  function updateContact(key: string, changes: Partial<AdditionalContactRow>) {
    setForm((current) => ({
      ...current,
      additionalContacts: current.additionalContacts.map((row) =>
        row.key === key ? { ...row, ...changes } : row,
      ),
    }))
  }

  function addContact() {
    rowSeq += 1
    setForm((current) =>
      current.additionalContacts.length >= MAX_ADDITIONAL_CONTACTS
        ? current
        : {
            ...current,
            additionalContacts: [...current.additionalContacts, emptyAdditionalContact(`ac-${rowSeq}`)],
          },
    )
  }

  function removeContact(key: string) {
    setForm((current) => ({
      ...current,
      additionalContacts: current.additionalContacts.filter((row) => row.key !== key),
    }))
  }

  function patchShipping(changes: Partial<Omit<IntakeFormState['shipping'], 'additionalShipTos'>>) {
    setForm((current) => ({ ...current, shipping: { ...current.shipping, ...changes } }))
  }

  function updateShipTo(key: string, address: string) {
    setForm((current) => ({
      ...current,
      shipping: {
        ...current.shipping,
        additionalShipTos: current.shipping.additionalShipTos.map((row) =>
          row.key === key ? { ...row, address } : row,
        ),
      },
    }))
  }

  function addShipTo() {
    rowSeq += 1
    setForm((current) =>
      current.shipping.additionalShipTos.length >= MAX_ADDITIONAL_SHIP_TOS
        ? current
        : {
            ...current,
            shipping: {
              ...current.shipping,
              additionalShipTos: [...current.shipping.additionalShipTos, emptyShipTo(`st-${rowSeq}`)],
            },
          },
    )
  }

  function removeShipTo(key: string) {
    setForm((current) => ({
      ...current,
      shipping: {
        ...current.shipping,
        additionalShipTos: current.shipping.additionalShipTos.filter((row) => row.key !== key),
      },
    }))
  }

  function patchOverview(changes: Partial<IntakeFormState['overview']>) {
    setForm((current) => ({ ...current, overview: { ...current.overview, ...changes } }))
  }

  function toggleRegion(region: Region) {
    setForm((current) => {
      const has = current.overview.regions.includes(region)
      const next = has
        ? current.overview.regions.filter((r) => r !== region)
        : REGIONS.filter((r) => current.overview.regions.includes(r) || r === region)
      return { ...current, overview: { ...current.overview, regions: next } }
    })
  }

  return (
    <div>
      <p className="micro-label">Green Line welcome packet</p>
      <h1 className="mt-1 text-[1.375rem] font-semibold tracking-wordmark text-ink">
        Welcome, {companyName}
      </h1>
      <WelcomeIntro className="mt-2 max-w-xl" />

      <section aria-labelledby="intake-process-heading" className="mt-8">
        <SectionHeader id="intake-process-heading" title="How working together runs" />
        <ProcessSteps variant="compact" className="mt-4" />
      </section>

      <section aria-labelledby="intake-directory-heading" className="mt-8">
        <SectionHeader id="intake-directory-heading" title="Who to write, from day one" />
        <ContactDirectory className="mt-4" />
      </section>

      <p className="mt-10 border-t border-rule/50 pt-6 text-sm leading-relaxed text-mute">
        Now the one ask: tell us about your company — billing details, your day-to-day contacts,
        and where shipments and samples should go. About five minutes, and no account or sign-in
        is needed.
      </p>

      {/* §1 Company information */}
      <section aria-labelledby="intake-company-heading" className="mt-8">
        <SectionHeader id="intake-company-heading" title="Company information" />
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <TextField
            id="company-legal-name"
            label="Legal company name"
            value={form.company.legalName}
            onChange={(legalName) => patchCompany({ legalName })}
            error={errors['company.legalName']}
            autoComplete="organization"
          />
          <TextField
            id="company-dba"
            label="DBA / trade name"
            value={form.company.dba}
            onChange={(dba) => patchCompany({ dba })}
            error={errors['company.dba']}
            hint="Optional."
          />
          <TextField
            id="company-document-name"
            label="Preferred document name"
            value={form.company.preferredDocumentName}
            onChange={(preferredDocumentName) => patchCompany({ preferredDocumentName })}
            error={errors['company.preferredDocumentName']}
            hint="Shown on invoices, shipping documents and order confirmations. Optional."
          />
          <TextField
            id="company-website"
            label="Website"
            value={form.company.website}
            onChange={(website) => patchCompany({ website })}
            error={errors['company.website']}
            autoComplete="url"
            hint="Optional."
          />
          <TextareaField
            id="company-billing-address"
            label="Billing address"
            value={form.company.billingAddress}
            onChange={(billingAddress) => patchCompany({ billingAddress })}
            error={errors['company.billingAddress']}
            rows={3}
            placeholder="Street, city, state, ZIP"
            className="sm:col-span-2"
          />
        </div>
      </section>

      {/* §2 Primary contacts — the four fixed role blocks */}
      <section aria-labelledby="intake-contacts-heading" className="mt-10">
        <SectionHeader id="intake-contacts-heading" title="Primary contacts" />
        <p className="mt-2 text-sm leading-relaxed text-mute">
          The four people we work with day to day — the same person can cover more than one role.
        </p>
        <div className="mt-4 space-y-4">
          {form.roleContacts.map((block, index) => {
            const slug = block.role.toLowerCase().replace(/\s+/g, '-')
            return (
              <Card key={block.role}>
                <p className="text-sm font-semibold text-ink">{block.role}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-mute">{ROLE_COPY[block.role]}</p>
                <div className="mt-4 grid gap-6 sm:grid-cols-3">
                  <TextField
                    id={`contact-${slug}-name`}
                    label="Name"
                    value={block.name}
                    onChange={(name) => patchRoleContact(index, { name })}
                    error={errors[`roleContacts.${index}.name`]}
                    autoComplete="off"
                  />
                  <TextField
                    id={`contact-${slug}-email`}
                    label="Email"
                    value={block.email}
                    onChange={(email) => patchRoleContact(index, { email })}
                    error={errors[`roleContacts.${index}.email`]}
                    type="email"
                    autoComplete="off"
                  />
                  <TextField
                    id={`contact-${slug}-phone`}
                    label="Phone"
                    value={block.phone}
                    onChange={(phone) => patchRoleContact(index, { phone })}
                    error={errors[`roleContacts.${index}.phone`]}
                    type="tel"
                    hint="Optional."
                  />
                </div>
              </Card>
            )
          })}
        </div>
      </section>

      {/* §3 Additional contacts (≤3, dynamic) */}
      <section aria-labelledby="intake-additional-heading" className="mt-10">
        <SectionHeader id="intake-additional-heading" title="Additional contacts" />
        <p className="mt-2 text-sm leading-relaxed text-mute">
          Anyone else we should keep in the loop — up to three.
        </p>
        <div className="mt-4 space-y-4">
          {form.additionalContacts.map((row, index) => (
            <Card key={row.key}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-ink">Additional contact {index + 1}</p>
                <button
                  type="button"
                  onClick={() => removeContact(row.key)}
                  aria-label={`Remove additional contact ${index + 1}`}
                  className="rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
                >
                  <X className="size-4" />
                </button>
              </div>
              <div className="mt-4 grid gap-6 sm:grid-cols-2">
                <TextField
                  id={`${row.key}-name`}
                  label="Name"
                  value={row.name}
                  onChange={(name) => updateContact(row.key, { name })}
                  error={errors[`additionalContacts.${index}.name`]}
                />
                <TextField
                  id={`${row.key}-title`}
                  label="Title"
                  value={row.title}
                  onChange={(title) => updateContact(row.key, { title })}
                  error={errors[`additionalContacts.${index}.title`]}
                  hint="Optional."
                />
                <TextField
                  id={`${row.key}-email`}
                  label="Email"
                  value={row.email}
                  onChange={(email) => updateContact(row.key, { email })}
                  error={errors[`additionalContacts.${index}.email`]}
                  type="email"
                  hint="Optional."
                />
                <TextField
                  id={`${row.key}-phone`}
                  label="Phone"
                  value={row.phone}
                  onChange={(phone) => updateContact(row.key, { phone })}
                  error={errors[`additionalContacts.${index}.phone`]}
                  type="tel"
                  hint="Optional."
                />
              </div>
            </Card>
          ))}
          {form.additionalContacts.length < MAX_ADDITIONAL_CONTACTS ? (
            <Button type="button" variant="quiet" onClick={addContact}>
              Add another contact
            </Button>
          ) : (
            <p className="text-xs text-mute">Three additional contacts is the most the intake takes.</p>
          )}
        </div>
      </section>

      {/* §4 Shipping */}
      <section aria-labelledby="intake-shipping-heading" className="mt-10">
        <SectionHeader id="intake-shipping-heading" title="Shipping" />
        <div className="mt-4 space-y-6">
          <TextareaField
            id="shipping-primary"
            label="Primary ship-to"
            value={form.shipping.primaryShipTo}
            onChange={(primaryShipTo) => patchShipping({ primaryShipTo })}
            error={errors['shipping.primaryShipTo']}
            rows={3}
            placeholder="Company name, street, city, state, ZIP"
          />
          {form.shipping.additionalShipTos.map((row, index) => (
            <div key={row.key} className="flex items-start gap-2">
              <TextareaField
                id={`ship-to-${row.key}`}
                label={`Additional ship-to ${index + 1}`}
                value={row.address}
                onChange={(address) => updateShipTo(row.key, address)}
                error={errors[`shipping.additionalShipTos.${index}`]}
                rows={2}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => removeShipTo(row.key)}
                aria-label={`Remove additional ship-to ${index + 1}`}
                className="mt-6 rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
          {form.shipping.additionalShipTos.length < MAX_ADDITIONAL_SHIP_TOS ? (
            <Button type="button" variant="quiet" onClick={addShipTo}>
              Add another ship-to
            </Button>
          ) : (
            <p className="text-xs text-mute">Four additional ship-tos is the most the intake takes.</p>
          )}
          <TextareaField
            id="shipping-sample"
            label="Sample ship-to"
            value={form.shipping.sampleShipTo}
            onChange={(sampleShipTo) => patchShipping({ sampleShipTo })}
            error={errors['shipping.sampleShipTo']}
            rows={2}
            hint="Where samples should go, if different. Optional."
          />
          <TextareaField
            id="shipping-requirements"
            label="Special delivery requirements"
            value={form.shipping.specialDeliveryRequirements}
            onChange={(specialDeliveryRequirements) => patchShipping({ specialDeliveryRequirements })}
            error={errors['shipping.specialDeliveryRequirements']}
            rows={2}
            hint="Delivery appointments, receiving hours, pallet or truck restrictions. Optional."
          />
        </div>
      </section>

      {/* §5 Business overview */}
      <section aria-labelledby="intake-overview-heading" className="mt-10">
        <SectionHeader id="intake-overview-heading" title="Business overview" />
        <div className="mt-4 space-y-7">
          <TextareaField
            id="overview-applications"
            label="Primary product applications"
            value={form.overview.primaryProductApplications}
            onChange={(primaryProductApplications) => patchOverview({ primaryProductApplications })}
            error={errors['overview.primaryProductApplications']}
            rows={3}
            placeholder="e.g. Facial serums, sports nutrition gummies"
            hint="The end products our ingredients go into — it helps us route samples and documentation."
          />

          <Field>
            <span id="overview-volume-label" className="micro-label block">
              Estimated annual volume
            </span>
            <RadioGroup
              aria-labelledby="overview-volume-label"
              value={form.overview.estimatedAnnualVolume || null}
              onValueChange={(estimatedAnnualVolume) => patchOverview({ estimatedAnnualVolume })}
              options={ANNUAL_VOLUMES.map((volume) => ({ value: volume, label: volume }))}
            />
            {errors['overview.estimatedAnnualVolume'] ? (
              <FieldError id="overview-volume-error">Choose one.</FieldError>
            ) : null}
          </Field>

          <Field>
            <span id="overview-regions-label" className="micro-label block">
              Regions you sell into
            </span>
            <div role="group" aria-labelledby="overview-regions-label" className="grid gap-2">
              {REGIONS.map((region) => (
                <Checkbox
                  key={region}
                  id={`region-${region.toLowerCase().replace(/\s+/g, '-')}`}
                  label={region}
                  checked={form.overview.regions.includes(region)}
                  onChange={() => toggleRegion(region)}
                />
              ))}
            </div>
            {errors['overview.regions'] ? (
              <FieldError id="overview-regions-error">Choose at least one region.</FieldError>
            ) : null}
          </Field>

          {form.overview.regions.includes('Other') ? (
            <TextField
              id="overview-regions-other"
              label="Other regions — describe"
              value={form.overview.regionsOther}
              onChange={(regionsOther) => patchOverview({ regionsOther })}
              error={errors['overview.regionsOther']}
              hint="Optional."
            />
          ) : null}
        </div>
      </section>

      {/* Footer */}
      <div className="mt-10 border-t border-rule/50 pt-6">
        <p className="text-sm leading-relaxed text-mute">
          Sending this intake shares it with your Green Line rep — they&rsquo;ll confirm next steps
          by email.
        </p>
        {attempted && Object.keys(errors).length > 0 ? (
          <p role="alert" className="mt-3 text-sm font-medium text-rust">
            Fix the highlighted fields to send your intake.
          </p>
        ) : null}
        {submitError ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust"
          >
            {submitError}
          </p>
        ) : null}
        <Button type="button" size="lg" className="mt-5" loading={mutation.isPending} onClick={send}>
          {mutation.isPending ? 'Sending…' : 'Send to Green Line'}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Field helpers — MicroLabel + Input/Textarea with error/hint wiring.
// ---------------------------------------------------------------------------

function describedBy(errorId?: string, hintId?: string): string | undefined {
  const ids = [errorId, hintId].filter(Boolean)
  return ids.length ? ids.join(' ') : undefined
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  placeholder,
  autoComplete,
  className,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  hint?: string
  type?: string
  placeholder?: string
  autoComplete?: string
  className?: string
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <Field className={className}>
      <MicroLabel htmlFor={id}>{label}</MicroLabel>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(errorId, hintId)}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </Field>
  )
}

function TextareaField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  rows = 3,
  placeholder,
  className,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  hint?: string
  rows?: number
  placeholder?: string
  className?: string
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <Field className={className}>
      <MicroLabel htmlFor={id}>{label}</MicroLabel>
      <Textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(errorId, hintId)}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </Field>
  )
}
